import { toast } from "@/components/common/Toast"
import * as Sentry from "@sentry/react"
import { fetchAuthSession } from "aws-amplify/auth"
import axios, {
  AxiosInstance,
  CanceledError,
  InternalAxiosRequestConfig,
} from "axios"
import {
  extractRequestIdFromError,
  generateRequestId,
} from "../../lib/requestId"
import { toastServerError } from "../../utils/errorToast"
import { triggerFlagRefresh } from "../../utils/featureFlagStore"
import { notifyLimitError, parseLimitError } from "../../utils/limitErrors"
import { serializeMetadataHeader } from "../../utils/metadata"
import { moduleTranslate } from "../../utils/moduleTranslate"
import {
  groupsOfBearer,
  refreshAfterTrialBlock,
} from "../../utils/upgradeSession"
import {
  convertDatesToDateStrings,
  convertDateStringsToDates,
  convertKeysToCamelCase,
  convertKeysToSnakeCase,
} from "../adapters/caseConversion/caseConversions"
import { trackApiCall, trackApiError } from "../analytics/autoTrack"
import { captureRequestEpoch, isRequestObsolete } from "./authRequestLifecycle"
import { getAccessToken } from "./authSession"
import {
  isIdempotentMethod,
  isNetworkError,
  MAX_NETWORK_RETRIES,
  reportNetworkFailure,
  waitUntilOnline,
} from "./connectivity"

// Extend config to carry request ID and start time for downstream interceptors
interface TimedAxiosRequestConfig extends InternalAxiosRequestConfig {
  __requestId?: string
  __startTime?: number
  __retried?: boolean
  __trialRetried?: boolean
  __authEpoch?: number
  __networkRetries?: number
}

const rejectObsolete = (config: TimedAxiosRequestConfig): void => {
  if (isRequestObsolete(config.__authEpoch ?? captureRequestEpoch())) {
    throw new CanceledError(
      "Request belongs to a previous session",
      undefined,
      config,
    )
  }
}

const AUTH_TOKEN_ERROR_TOAST_ID = "auth-token-error"

// Language header - QMS i18n localizes by Accept-Language; keep it aligned
// with the app language instead of the raw browser locale.
/** Attaches the bearer token. Exported standalone for the same reason
 *  `transformResponseData` is: it carries real behaviour (the session-race
 *  retry and the wait for the network, both in `getAccessToken`) that
 *  deserves a test without spinning up axios. */
export async function applyAuthToken<T extends InternalAxiosRequestConfig>(
  config: T,
): Promise<T> {
  const lifecycleConfig = config as T & TimedAxiosRequestConfig
  lifecycleConfig.__authEpoch ??= captureRequestEpoch()
  try {
    const token = await getAccessToken({ epoch: lifecycleConfig.__authEpoch })
    config.headers.Authorization = `Bearer ${token}`
  } catch (error) {
    if (
      axios.isCancel(error) ||
      isRequestObsolete(lifecycleConfig.__authEpoch)
    ) {
      return Promise.reject(
        new CanceledError(
          "Request belongs to a previous session",
          undefined,
          config,
        ),
      )
    }
    if (error instanceof Error && !(error as Error & { config?: T }).config) {
      ;(error as Error & { config?: T }).config = config
    }
    // `getAccessToken` already waited for the network. A refresh that still
    // never reached Cognito is not an authentication error: Amplify keeps
    // the tokens and the user stays signed in.
    if (!isNetworkError(error)) {
      // One id for every request: a burst of parallel queries failing on the
      // same refresh shows one toast instead of one per request.
      toast.error(moduleTranslate("USER_ERROR_AUTH_TOKEN"), {
        id: AUTH_TOKEN_ERROR_TOAST_ID,
      })
    }
    return Promise.reject(error)
  }
  return config
}

export const applyLanguageHeader = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  try {
    const lang = (localStorage.getItem("language") ?? "DE").toLowerCase()
    if (config.headers.set) {
      config.headers.set("Accept-Language", lang)
    } else {
      ;(config.headers as Record<string, string>)["Accept-Language"] = lang
    }
  } catch {
    // never block the request
  }
  return config
}

const ID_SEGMENT =
  /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

/** Path params that are neither UUIDs nor numbers. Each is replaced only
 *  directly after its endpoint's path (`after`) and only in its own shape, so
 *  a static sibling route (`declaration-templates/variables`) or the same
 *  shape under another endpoint stays as it is: distinct endpoints never
 *  merge into one issue. */
const KEYED_PARAMS: { after: string[]; name: string; shape: RegExp }[] = [
  {
    // Template keys: `<name>-<6 hex>` (mc_declaration_template_service._slug)
    // or the built-in `default`.
    after: ["material-compliance", "declaration-templates"],
    name: ":key",
    shape: /^(default|[a-z0-9][a-z0-9-]*-[0-9a-f]{6})$/,
  },
]

/** A request's path with its ids replaced (`/api/v1/kb-categories/:id/tags`),
 *  so an endpoint groups into one Sentry issue whichever resource it hit.
 *  Query strings are dropped for the same reason. */
const routeTemplate = (uri: string): string => {
  const segments = new URL(uri, "http://localhost").pathname.split("/")
  return segments
    .map((segment, index) => {
      if (ID_SEGMENT.test(segment)) return ":id"
      const keyed = KEYED_PARAMS.find(
        ({ after, shape }) =>
          shape.test(segment) &&
          after.every(
            (part, offset) => segments[index - after.length + offset] === part,
          ),
      )
      return keyed ? keyed.name : segment
    })
    .join("/")
}

function createApiClient(baseURL: string): AxiosInstance {
  const client = axios.create({ baseURL })

  // Request ID interceptor - MUST be first to generate ID for all subsequent interceptors
  client.interceptors.request.use(
    (config) => {
      const requestId = generateRequestId()
      config.headers["X-Request-Id"] = requestId
      ;(config as TimedAxiosRequestConfig).__requestId = requestId
      return config
    },
    (error) => Promise.reject(error),
  )

  // Metadata header interceptor - after requestId, before auth
  client.interceptors.request.use(
    (config) => {
      try {
        const headers = config.headers
        const metadata = serializeMetadataHeader()
        if (headers.set) {
          headers.set("X-Event-Metadata", metadata)
        } else {
          headers["X-Event-Metadata"] = metadata
        }
      } catch {
        // never block the request
      }
      return config
    },
    (error) => Promise.reject(error),
  )

  // Language header interceptor - after metadata, before auth
  client.interceptors.request.use(applyLanguageHeader, (error) =>
    Promise.reject(error),
  )

  // Authentication interceptor
  client.interceptors.request.use(applyAuthToken, (error) =>
    Promise.reject(error),
  )

  // Case conversion interceptor
  client.interceptors.request.use(
    (config) => {
      // Same opt-out as the response side: a body whose OBJECT KEYS carry data
      // (e.g. column overrides keyed by spreadsheet header) sets
      // `skipCaseConversion` and ships snake_case field names itself. Without
      // it "Warengruppe" would leave here as "_warengruppe" and never match.
      const skipCaseConversion = Boolean(
        (config as { skipCaseConversion?: boolean }).skipCaseConversion,
      )
      if (
        config.data &&
        !(config.data instanceof FormData) &&
        !skipCaseConversion
      ) {
        config.data = convertDatesToDateStrings(config.data)
        config.data = convertKeysToSnakeCase(config.data)
      }
      return config
    },
    (error) => Promise.reject(error),
  )

  // Timing interceptor - stamps start time after all request transforms
  client.interceptors.request.use(
    (config) => {
      const lifecycleConfig = config as TimedAxiosRequestConfig
      lifecycleConfig.__authEpoch ??= captureRequestEpoch()
      rejectObsolete(lifecycleConfig)
      ;(config as TimedAxiosRequestConfig).__startTime = Date.now()
      return config
    },
    (error) => Promise.reject(error),
  )

  client.interceptors.response.use(
    (response) => {
      rejectObsolete(response.config as TimedAxiosRequestConfig)
      // Skip transformation for binary / non-JSON payloads (e.g., PDFs, blobs, array buffers)
      const isBlob =
        typeof Blob !== "undefined" && response.data instanceof Blob
      const isArrayBuffer = response.data instanceof ArrayBuffer
      const isString = typeof response.data === "string"
      // Heuristic: only transform plain objects/arrays
      const isPlainObjectOrArray =
        response.data &&
        typeof response.data === "object" &&
        !isBlob &&
        !isArrayBuffer &&
        !ArrayBuffer.isView(response.data)

      // Opt-out (default off): an endpoint whose response carries meaningful
      // data in its OBJECT KEYS — e.g. the MC read-model, keyed
      // `framework:articleNumber` — sets `skipCaseConversion` so the snake→camel
      // pass doesn't mangle those keys (an article number containing `_x` would
      // otherwise be corrupted). Existing calls are unaffected.
      const skipCaseConversion = Boolean(
        (response.config as { skipCaseConversion?: boolean })
          ?.skipCaseConversion,
      )
      if (isPlainObjectOrArray && !isString && !skipCaseConversion) {
        let data = convertKeysToCamelCase(response.data)
        data = convertDateStringsToDates(data)
        response.data = data
      }

      // Auto-track successful API call
      try {
        const config = response.config as TimedAxiosRequestConfig
        const durationMs = config.__startTime
          ? Date.now() - config.__startTime
          : 0
        trackApiCall(
          config.method?.toUpperCase() ?? "UNKNOWN",
          config.url ?? "",
          response.status,
          durationMs,
        )
      } catch {
        // never block response
      }

      // No Sentry context here: setContext is page-wide, so every later,
      // unrelated crash would carry whichever request finished last. Sentry's
      // XHR breadcrumbs already record each request in order.
      return response
    },
    async (error) => {
      if (
        axios.isCancel(error) ||
        (error?.config &&
          isRequestObsolete(
            (error.config as TimedAxiosRequestConfig).__authEpoch ??
              captureRequestEpoch(),
          ))
      ) {
        return Promise.reject(
          axios.isCancel(error)
            ? error
            : new CanceledError(
                "Request belongs to a previous session",
                undefined,
                error.config,
              ),
        )
      }
      // No response at all: the network is down (a tab waking from sleep
      // before Wi-Fi is back). Every request now waits for the network; a
      // read is resent once it is back. A write is not, it may have reached
      // the server.
      if (axios.isAxiosError(error) && isNetworkError(error) && error.config) {
        reportNetworkFailure()
        const retryConfig = error.config as TimedAxiosRequestConfig
        const networkRetries = retryConfig.__networkRetries ?? 0
        if (
          isIdempotentMethod(retryConfig.method) &&
          networkRetries < MAX_NETWORK_RETRIES &&
          (await waitUntilOnline())
        ) {
          rejectObsolete(retryConfig)
          retryConfig.__networkRetries = networkRetries + 1
          return client(retryConfig)
        }
      }

      // Attach request ID to error for easy access in error handlers
      const requestId = extractRequestIdFromError(error)
      if (error && requestId) {
        error.requestId = requestId
      }

      const status = error?.response?.status
      const method = error?.config?.method?.toUpperCase() ?? "UNKNOWN"
      const url = error?.config?.url ?? ""

      // Auto-track API error
      try {
        trackApiError(method, url, status, requestId, error?.message)
      } catch {
        // never block error handling
      }

      // Retry once on 401 with a force-refreshed session token
      if (
        status === 401 &&
        !(error?.config as TimedAxiosRequestConfig)?.__retried
      ) {
        try {
          const session = await fetchAuthSession({ forceRefresh: true })
          rejectObsolete(error.config as TimedAxiosRequestConfig)
          const token = session.tokens?.accessToken.toString()
          if (token) {
            const retryConfig = error.config as TimedAxiosRequestConfig
            retryConfig.__retried = true
            retryConfig.headers.Authorization = `Bearer ${token}`
            return client(retryConfig)
          }
        } catch {
          // fall through to normal error handling
        }
        if (
          isRequestObsolete(
            (error.config as TimedAxiosRequestConfig).__authEpoch ??
              captureRequestEpoch(),
          )
        ) {
          return Promise.reject(
            new CanceledError(
              "Request belongs to a previous session",
              undefined,
              error.config,
            ),
          )
        }
      }

      // Structured usage-limit (429) and trial (403) bodies get a limit
      // toast instead of a generic error
      const limitError = parseLimitError(status, error?.response?.data)

      // A limit or trial error can come from a stale token: the user paid but
      // the token still lists the trial group (TRIAL_EXPIRED, and while an
      // upgrade link is open also a 429 or TRIAL_FEATURE_UNAVAILABLE), or it
      // was issued without a trial clock (TRIAL_STATUS_UNKNOWN). One forced
      // refresh serves all parallel requests; if it cures the token, send the
      // request again instead of the toast. A request is retried once.
      const refusedConfig = error?.config as TimedAxiosRequestConfig | undefined
      const trialRefresh = refusedConfig?.__trialRetried
        ? null
        : refreshAfterTrialBlock(limitError?.kind, {
            startedAt: refusedConfig?.__startTime,
            sentGroups: groupsOfBearer(refusedConfig?.headers?.Authorization),
          })
      if (trialRefresh) {
        try {
          if (await trialRefresh) {
            rejectObsolete(error.config as TimedAxiosRequestConfig)
            const retryConfig = error.config as TimedAxiosRequestConfig
            retryConfig.__trialRetried = true
            return client(retryConfig)
          }
        } catch {
          // fall through to the limit toast
        }
        if (
          isRequestObsolete(
            (error.config as TimedAxiosRequestConfig).__authEpoch ??
              captureRequestEpoch(),
          )
        ) {
          return Promise.reject(
            new CanceledError(
              "Request belongs to a previous session",
              undefined,
              error.config,
            ),
          )
        }
      }

      // Toast for 5xx errors — suppress for feature-flag-disabled responses
      let isFeatureDisabled = false
      try {
        if (status) {
          const detail = error?.response?.data?.detail
          isFeatureDisabled =
            status === 403 &&
            typeof detail === "object" &&
            detail !== null &&
            (detail as { error_code?: string }).error_code ===
              "FEATURE_DISABLED"

          if (isFeatureDisabled) {
            triggerFlagRefresh()
          } else if (limitError) {
            notifyLimitError(limitError)
          } else if (
            !(error?.config as { skipErrorToast?: boolean } | undefined)
              ?.skipErrorToast
          ) {
            toastServerError(status, requestId)
          }
        }
      } catch {
        // never block error handling
      }

      // Report 4xx client errors to Sentry, one issue per endpoint and status:
      // a single static issue for every 4xx was never new and stayed low
      // priority, so a form the API kept rejecting alerted nobody (TUR-1770).
      // 400/422 mean the API refused the request itself, almost always a
      // frontend/backend mismatch, so they are warnings; other 4xx are mostly
      // outcomes the UI handles and stay info. Skip 401 (handled by the retry
      // above), feature-disabled 403 (already suppressed from the user) and
      // expected limit hits (they show a limit toast and are not client bugs).
      try {
        if (
          status >= 400 &&
          status < 500 &&
          status !== 401 &&
          !isFeatureDisabled &&
          !limitError
        ) {
          const route = routeTemplate(client.getUri(error.config))
          Sentry.captureMessage(`API ${status} ${method} ${route}`, {
            level: status === 400 || status === 422 ? "warning" : "info",
            fingerprint: ["api-client-error", method, route, String(status)],
            tags: {
              "http.status_code": String(status),
              "http.method": method,
              "api.route": route,
              ...(requestId ? { request_id: requestId } : {}),
            },
            extra: { url },
          })
        }
      } catch {
        // never block error handling
      }

      return Promise.reject(error)
    },
  )

  return client
}

const baseURL =
  import.meta.env.VITE_APP_API_BASE_URL ?? "http://localhost:8000/api/v1"

const baseURLV2 =
  import.meta.env.VITE_APP_API_BASE_URL_V2 ??
  baseURL.replace(/\/api\/v1$/, "/api/v2")

const apiClient = createApiClient(baseURL)
export const apiClientV2 = createApiClient(baseURLV2)

export default apiClient
